import { useEffect, useMemo, useState } from 'react';
import { useNavigate, useOutletContext, useSearchParams } from 'react-router-dom';
import { ventesApi, meApi } from '../api/endpoints';
import type {
  LodeomTypeCode, Origine, ProdCode, ScpiModeCode, UserDto, VenteDto
} from '../api/types';
import { useToast } from '../hooks/useToast';
import {
  AGENCES, DISPOSITIFS, formulaText, previewCollecte, previewCommission,
  previewScore, requiresFrais, tauxInfoText
} from '../lib/commission';
import { fireConfetti } from '../lib/confetti';
import { fmtEur, todayIso } from '../lib/format';
import { SuccessModal } from '../components/SuccessModal';

// ─── Catalogue produits (libellés + sous-titres visuels) ──────────
interface ProductDef {
  code: ProdCode;
  icon: string;
  label: string;
  rate: string;
}
const PRODUCTS_SRX: ProductDef[] = [
  { code: 'pu',     icon: '🛡️', label: 'Prime Unique',     rate: 'ASV / PER' },
  { code: 'pp',     icon: '📅', label: 'Prime Périodique', rate: 'Versements programmés' },
  { code: 'lodeom', icon: '🌴', label: 'LODEOM',           rate: 'Min. 2 500 €' }
];
const PRODUCTS_QUIN: ProductDef[] = [
  { code: 'immo-vente',  icon: '🏠', label: 'Vente immobilière', rate: 'Défiscalisation' },
  { code: 'immo-apport', icon: '🤝', label: "Apport d'affaires", rate: 'Défiscalisation' },
  { code: 'scpi',        icon: '🏢', label: 'SCPI',              rate: 'Cash ou à crédit' }
];

interface OutletCtx { user?: UserDto }

export function DeclarerPage() {
  const { user } = useOutletContext<OutletCtx>();
  const toast = useToast();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const editId = params.get('edit');
  const isEdit = !!editId;

  // ─── État du formulaire ────────────────────────────
  const [origine, setOrigine] = useState<Origine>('leads');
  const [prod, setProd] = useState<ProdCode>('pu');
  const [montant, setMontant] = useState<string>('');
  const [frais, setFrais] = useState<string>('3.5');
  const [tauxImmo, setTauxImmo] = useState<string>('2.5');  // taux % pour immo-vente
  const [lodeomType, setLodeomType] = useState<LodeomTypeCode>('new');
  const [scpiMode, setScpiMode] = useState<ScpiModeCode>('cash');
  const [dispositif, setDispositif] = useState<string>('Accession');
  const [clientPrenom, setClientPrenom] = useState('');
  const [clientNom, setClientNom] = useState('');
  const [agence, setAgence] = useState<string>('');
  const [dateVente, setDateVente] = useState<string>(todayIso());
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);

  // ─── Modal de succès après création ─────────────────
  const [successVente, setSuccessVente] = useState<VenteDto | null>(null);
  const [successHighlight, setSuccessHighlight] = useState<string | null>(null);

  // Initialiser agence avec celle du user
  useEffect(() => {
    if (user && !agence) setAgence(user.agence);
  }, [user, agence]);

  // Auto-remplir le taux immobilier par défaut quand l'origine change.
  // (uniquement quand on est sur immo-vente et qu'on n'est pas en édition)
  useEffect(() => {
    if (prod === 'immo-vente' && !isEdit) {
      setTauxImmo(origine === 'reco' ? '4' : '2.5');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [origine, prod]);

  // ─── Mode édition : charger la vente existante ─────
  useEffect(() => {
    if (!editId) return;
    ventesApi.get(editId).then(v => {
      setOrigine(v.origine);
      setProd(v.prod);
      setMontant(String(v.montant));
      if (v.frais != null) setFrais(String(v.frais));
      if (v.tauxImmoVente != null) setTauxImmo(String(v.tauxImmoVente));
      else if (v.prod === 'immo-vente') setTauxImmo(v.origine === 'reco' ? '4' : '2.5');
      if (v.lodeomType) setLodeomType(v.lodeomType);
      if (v.scpiMode) setScpiMode(v.scpiMode);
      if (v.dispositif) setDispositif(v.dispositif);
      setClientPrenom(v.clientPrenom);
      setClientNom(v.clientNom);
      setAgence(v.agence);
      setDateVente(v.dateVente);
      setNotes(v.notes ?? '');
    }).catch(() => {
      toast('Impossible de charger la vente', 'error');
      navigate('/declarer', { replace: true });
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId]);

  // ─── Calculs preview ───────────────────────────────
  const numericMontant = useMemo(() => {
    const m = parseFloat(montant.replace(',', '.'));
    return isNaN(m) ? 0 : m;
  }, [montant]);

  const numericFrais = useMemo(() => {
    const f = parseFloat(frais.replace(',', '.'));
    return isNaN(f) ? 3.5 : f;
  }, [frais]);

  /**
   * Taux % manuel pour immo-vente, ou null si égal au défaut (Leads = 2.5, Reco = 4).
   * On normalise à null quand l'utilisateur n'a pas dérogé au défaut, pour ne pas
   * polluer la DB avec une redondance.
   */
  const numericTauxImmo = useMemo(() => {
    const t = parseFloat(tauxImmo.replace(',', '.'));
    if (isNaN(t) || t <= 0) return null;
    const defaut = origine === 'reco' ? 4 : 2.5;
    return Math.abs(t - defaut) < 0.001 ? null : t;
  }, [tauxImmo, origine]);

  const preview = useMemo(() => {
    if (!user || numericMontant <= 0) return null;
    const com = previewCommission({
      prod,
      montant: numericMontant,
      statut: user.statut,
      origine,
      frais: requiresFrais(prod) ? numericFrais : null,
      lodeomType: prod === 'lodeom' ? lodeomType : null,
      tauxImmoVente: prod === 'immo-vente' ? numericTauxImmo : null
    });
    const col = previewCollecte(prod, numericMontant);
    const sc = previewScore(com, col);
    return { com, col, sc };
  }, [user, numericMontant, prod, origine, numericFrais, lodeomType, numericTauxImmo]);

  // ─── Soumission ────────────────────────────────────
  async function onSubmit() {
    if (!clientPrenom.trim() || !clientNom.trim()) {
      toast('Prénom et nom du client requis', 'error');
      return;
    }
    if (numericMontant <= 0) {
      toast('Montant invalide', 'error');
      return;
    }
    if (requiresFrais(prod) && (numericFrais < 1 || numericFrais > 4.5)) {
      toast('Frais entre 1 % et 4,5 %', 'error');
      return;
    }
    if (!agence) {
      toast('Choisis une agence', 'error');
      return;
    }

    setSubmitting(true);
    try {
      const payload = {
        clientPrenom: clientPrenom.trim(),
        clientNom: clientNom.trim(),
        prod,
        origine,
        montant: numericMontant,
        frais: requiresFrais(prod) ? numericFrais : null,
        tauxImmoVente: prod === 'immo-vente' ? numericTauxImmo : null,
        lodeomType: prod === 'lodeom' ? lodeomType : null,
        scpiMode: prod === 'scpi' ? scpiMode : null,
        dispositif: isImmoProd(prod) ? dispositif : null,
        notes: notes.trim() || null,
        dateVente,
        agence
      };
      if (isEdit) {
        await ventesApi.update(editId!, payload);
        toast('Vente modifiée', 'success');
        navigate('/ventes');
      } else {
        const created = await ventesApi.create(payload);
        // 🎉 Confettis sur création (pas sur édition)
        fireConfetti({ origin: 'top', count: 80 });

        // Vérifie si l'objectif mensuel a été atteint pour afficher un highlight festif
        let highlight: string | null = null;
        try {
          const me = await meApi.get();
          if (me.stats.targetCom && me.stats.targetCom > 0 && me.stats.progressPct >= 100) {
            highlight = '🎯 Objectif du mois atteint !';
          }
        } catch { /* silencieux */ }

        setSuccessHighlight(highlight);
        setSuccessVente(created);
      }
    } catch (err) {
      toast(err instanceof Error ? err.message : 'Enregistrement impossible', 'error');
    } finally {
      setSubmitting(false);
    }
  }

  function cancelEdit() {
    navigate('/declarer', { replace: true });
    window.location.reload(); // reset propre
  }

  // ─── Handlers de la modal de succès ─────────────────
  function handleNewSale() {
    setSuccessVente(null);
    setSuccessHighlight(null);
    // Reset minimal du formulaire — on garde origine/produit/agence pour
    // accélérer les déclarations en rafale du même type
    setClientPrenom('');
    setClientNom('');
    setMontant('');
    setNotes('');
    setDateVente(todayIso());
  }

  function handleSeeSales() {
    setSuccessVente(null);
    setSuccessHighlight(null);
    navigate('/ventes');
  }

  // ─── Libellés dynamiques ───────────────────────────
  const isImmo = isImmoProd(prod);
  const montantLabel = isImmo ? 'Prix immobilier TTC (€)' : 'Montant souscrit (€)';
  const isPP = prod === 'pp';

  const formula = useMemo(() => {
    if (!user) return null;
    return formulaText({
      prod,
      statut: user.statut,
      origine,
      frais: requiresFrais(prod) ? numericFrais : null,
      lodeomType: prod === 'lodeom' ? lodeomType : null,
      tauxImmoVente: prod === 'immo-vente' ? numericTauxImmo : null
    });
  }, [prod, origine, numericFrais, lodeomType, numericTauxImmo, user]);

  const tauxInfo = useMemo(() => {
    if (!user) return null;
    return tauxInfoText({
      prod,
      statut: user.statut,
      origine,
      frais: requiresFrais(prod) ? numericFrais : null,
      lodeomType: prod === 'lodeom' ? lodeomType : null,
      tauxImmoVente: prod === 'immo-vente' ? numericTauxImmo : null
    });
  }, [prod, origine, numericFrais, lodeomType, numericTauxImmo, user]);

  // ─── Render ────────────────────────────────────────
  return (
    <div className="py-4 space-y-4">

      {/* ─── Bandeau édition ─── */}
      {isEdit && (
        <div className="flex items-center justify-between gap-2 bg-warn-bg border border-warn/40 rounded-lg px-3 py-2 text-sm text-warn">
          <span><strong>Modification en cours</strong> · les changements remplacent la vente existante</span>
          <button
            type="button"
            onClick={cancelEdit}
            className="text-xs underline hover:opacity-70"
          >
            Annuler
          </button>
        </div>
      )}

      {/* ─── Origine ─── */}
      <Card title="Origine de la vente">
        <PillRow>
          <Pill selected={origine === 'leads'} onClick={() => setOrigine('leads')}>📋 Leads</Pill>
          <Pill selected={origine === 'reco'}  onClick={() => setOrigine('reco')}>🤝 Recommandation</Pill>
        </PillRow>
      </Card>

      {/* ─── Type de produit ─── */}
      <Card title="Type de produit">
        {/* Section Soronext */}
        <SectionHeader emoji="💰" entityName="Soronext" entityTag="Placements financiers" />
        <ProductGrid>
          {PRODUCTS_SRX.map(p => (
            <ProductChip
              key={p.code}
              icon={p.icon} label={p.label} rate={p.rate}
              selected={prod === p.code}
              onClick={() => setProd(p.code)}
            />
          ))}
        </ProductGrid>

        {/* Section Quintésens */}
        <SectionHeader
          emoji="🏛️" entityName="Quintésens" entityTag="Investissement immobilier" qts
          className="mt-4"
        />
        <ProductGrid>
          {PRODUCTS_QUIN.map(p => (
            <ProductChip
              key={p.code}
              icon={p.icon} label={p.label} rate={p.rate}
              selected={prod === p.code}
              onClick={() => setProd(p.code)}
            />
          ))}
        </ProductGrid>

        {/* ─── Sub-options contextuelles ─── */}

        {/* PU : pas de pills, juste la formule (le champ frais est plus bas) */}
        {prod === 'pu' && (
          <SubOpt label="Frais d'entrée">
            <FormulaBox>{formula}</FormulaBox>
          </SubOpt>
        )}

        {/* PP : tranche auto selon frais */}
        {prod === 'pp' && (
          <SubOpt
            label={<>Tranche de commission <span className="font-light opacity-60">(auto · selon frais)</span></>}
          >
            <PillRow readOnly className="mb-2">
              <Pill selected={numericFrais >= 3.95} readOnly>≥ 3,95% → ×2,5</Pill>
              <Pill selected={numericFrais < 3.95}  readOnly>&lt; 3,95% → ×1,0</Pill>
            </PillRow>
            <FormulaBox>{formula}</FormulaBox>
          </SubOpt>
        )}

        {/* LODEOM : pills + formule */}
        {prod === 'lodeom' && (
          <SubOpt label="Type de souscription">
            <PillRow className="mb-2">
              <Pill selected={lodeomType === 'new'}  onClick={() => setLodeomType('new')}>Nouveau client — 3%</Pill>
              <Pill selected={lodeomType === 'reab'} onClick={() => setLodeomType('reab')}>Ré-abondement — 2%</Pill>
            </PillRow>
            <FormulaBox>{formula}</FormulaBox>
          </SubOpt>
        )}

        {/* IMMO-VENTE : champ taux personnalisable (cas rares 3%, 3,5%...) */}
        {prod === 'immo-vente' && (
          <SubOpt label="Taux de commission (%)">
            <div className="flex items-center gap-3 mb-2">
              <input
                type="number"
                min="0.5" max="10" step="0.5"
                inputMode="decimal"
                value={tauxImmo}
                onChange={e => setTauxImmo(e.target.value)}
                className="input flex-1"
              />
              <span className="text-xs text-ink-sub font-light shrink-0">
                Défaut : {origine === 'reco' ? '4 %' : '2,5 %'}
              </span>
            </div>
            <FormulaBox>{formula}</FormulaBox>
          </SubOpt>
        )}

        {/* IMMO (vente / apport) : pills dispositif + formule */}
        {(prod === 'immo-vente' || prod === 'immo-apport') && (
          <SubOpt label="Dispositif fiscal">
            <div className="flex flex-wrap gap-2 mb-2">
              {DISPOSITIFS.map(d => (
                <Pill
                  key={d}
                  selected={dispositif === d}
                  onClick={() => setDispositif(d)}
                >
                  {d}
                </Pill>
              ))}
            </div>
            {/* Pour immo-apport, on garde la formule ici. Pour immo-vente, elle est déjà au-dessus. */}
            {prod === 'immo-apport' && <FormulaBox>{formula}</FormulaBox>}
          </SubOpt>
        )}

        {/* SCPI : pills mode + formule */}
        {prod === 'scpi' && (
          <SubOpt label="Mode de financement">
            <PillRow className="mb-2">
              <Pill selected={scpiMode === 'cash'}   onClick={() => setScpiMode('cash')}>💵 Cash</Pill>
              <Pill selected={scpiMode === 'credit'} onClick={() => setScpiMode('credit')}>🏦 À crédit</Pill>
            </PillRow>
            <FormulaBox>{formula}</FormulaBox>
          </SubOpt>
        )}

        {/* Info taux dynamique 💡 (toujours affichée) */}
        {tauxInfo && (
          <div className="mt-3 flex items-center gap-2 text-xs text-ink-sub bg-surface-muted rounded-lg px-3 py-2">
            <span>💡</span>
            <span>{tauxInfo}</span>
          </div>
        )}
      </Card>

      {/* ─── Informations ─── */}
      <Card title="Informations">
        <div className="grid grid-cols-2 gap-3">
          <Field label="Prénom client">
            <input
              type="text" autoComplete="off"
              placeholder="Jean"
              value={clientPrenom}
              onChange={e => setClientPrenom(e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Nom client">
            <input
              type="text" autoComplete="off"
              placeholder="Dupont"
              value={clientNom}
              onChange={e => setClientNom(e.target.value)}
              className="input"
            />
          </Field>
        </div>

        <div className={`grid ${requiresFrais(prod) ? 'grid-cols-2' : 'grid-cols-1'} gap-3 mt-3`}>
          <Field label={montantLabel}>
            <input
              type="number"
              min="0" max="10000000" step="100"
              inputMode="numeric"
              placeholder="0"
              value={montant}
              onChange={e => setMontant(e.target.value)}
              className="input"
            />
            {/* Hint : pour PP, affiche l'annualisation */}
            {isPP && numericMontant > 0 && (
              <div className="text-[11px] text-ink-sub mt-1">
                Annualisé : {fmtEur(numericMontant * 12)} / an
              </div>
            )}
          </Field>

          {requiresFrais(prod) && (
            <Field label="Frais d'entrée (%)">
              <input
                type="number"
                min="1" max="4.5" step="0.5"
                inputMode="decimal"
                placeholder="3.5"
                value={frais}
                onChange={e => setFrais(e.target.value)}
                className="input"
              />
            </Field>
          )}
        </div>

        <div className="grid grid-cols-2 gap-3 mt-3">
          <Field label="Date">
            <input
              type="date"
              value={dateVente}
              onChange={e => setDateVente(e.target.value)}
              className="input"
            />
          </Field>
          <Field label="Agence">
            <select
              value={agence}
              onChange={e => setAgence(e.target.value)}
              className="input"
            >
              <option value="">Sélectionner…</option>
              {AGENCES.map(a => <option key={a} value={a}>{a}</option>)}
            </select>
          </Field>
        </div>

        <Field label="Notes" className="mt-3">
          <textarea
            placeholder="Produit spécifique, contexte client…"
            rows={2}
            value={notes}
            onChange={e => setNotes(e.target.value)}
            className="input resize-none"
          />
        </Field>
      </Card>

      {/* ─── Preview commission ─── */}
      {preview && (
        <div className="bg-teal-bg border border-teal/30 rounded-xl px-4 py-3">
          <div className="text-[11px] uppercase tracking-wider text-ink-sub font-demi mb-2">
            Estimation en direct
          </div>
          <div className="grid grid-cols-3 gap-2">
            <PreviewItem label="Commission" value={fmtEur(preview.com)} color="text-teal-dark" />
            <PreviewItem label="Collecte"   value={fmtEur(preview.col)} color="text-srx" />
            <PreviewItem label="Score"      value={`${preview.sc} pts`} color="text-ink" />
          </div>
        </div>
      )}

      {/* ─── Bouton submit ─── */}
      <button
        type="button"
        onClick={onSubmit}
        disabled={submitting}
        className="w-full py-3 rounded-xl bg-teal text-white font-demi hover:bg-teal-dark transition-colors disabled:opacity-60"
      >
        {submitting
          ? 'Enregistrement…'
          : isEdit ? 'Mettre à jour la vente' : 'Enregistrer la vente'}
      </button>

      {/* ─── Modal succès "Vente enregistrée" (style Unibet) ─── */}
      {successVente && (
        <SuccessModal
          vente={successVente}
          highlight={successHighlight}
          onNewSale={handleNewSale}
          onSeeSales={handleSeeSales}
        />
      )}
    </div>
  );
}

// ─── Helpers ───────────────────────────────────────────────────────

function isImmoProd(p: ProdCode): boolean {
  return p === 'immo-vente' || p === 'immo-apport' || p === 'scpi';
}

// ─── Sous-composants ───────────────────────────────────────────────

function Card({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="card">
      <div className="text-[11px] uppercase tracking-wider text-ink-sub font-demi mb-3">
        {title}
      </div>
      {children}
    </div>
  );
}

function SectionHeader({
  emoji, entityName, entityTag, qts, className
}: {
  emoji: string;
  entityName: string;
  entityTag: string;
  qts?: boolean;
  className?: string;
}) {
  const colorCls = qts
    ? 'text-qts-dark border-qts/30'
    : 'text-srx-dark border-srx/30';
  return (
    <div className={`flex items-center gap-2 mb-2 ${className ?? ''}`}>
      <span className="text-base">{emoji}</span>
      <span className={`px-2 py-0.5 rounded text-xs font-demi border bg-white ${colorCls}`}>
        {entityName}
      </span>
      <span className="text-xs text-ink-sub font-light">{entityTag}</span>
    </div>
  );
}

function ProductGrid({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-3 gap-2">{children}</div>;
}

function ProductChip({
  icon, label, rate, selected, onClick
}: {
  icon: string;
  label: string;
  rate: string;
  selected: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`p-3 rounded-lg border text-left transition-colors
        ${selected
          ? 'border-teal bg-teal-bg ring-2 ring-teal/20'
          : 'border-border bg-surface hover:bg-surface-muted'}`}
    >
      <div className="text-xl">{icon}</div>
      <div className="text-xs font-demi text-ink leading-snug mt-1.5">{label}</div>
      <div className="text-[11px] text-ink-sub mt-1 leading-snug font-light">{rate}</div>
    </button>
  );
}

function SubOpt({ label, children }: { label: React.ReactNode; children: React.ReactNode }) {
  return (
    <div className="mt-3 p-3 bg-surface-muted rounded-lg">
      <div className="text-[11px] font-demi text-ink-sub mb-2">{label}</div>
      {children}
    </div>
  );
}

function FormulaBox({ children }: { children: React.ReactNode }) {
  return (
    <div className="font-mono text-[11px] text-teal-dark bg-teal-bg border border-teal/30 rounded-md px-3 py-2">
      {children}
    </div>
  );
}

function PillRow({
  children, readOnly, className
}: { children: React.ReactNode; readOnly?: boolean; className?: string }) {
  return (
    <div className={`flex gap-2 ${readOnly ? 'pointer-events-none' : ''} ${className ?? ''}`}>
      {children}
    </div>
  );
}

function Pill({
  children, selected, onClick, readOnly
}: {
  children: React.ReactNode;
  selected?: boolean;
  onClick?: () => void;
  readOnly?: boolean;
}) {
  return (
    <button
      type="button"
      onClick={readOnly ? undefined : onClick}
      tabIndex={readOnly ? -1 : 0}
      className={`px-3 py-1.5 rounded-full text-sm border transition-colors
        ${selected
          ? 'bg-teal text-white border-teal'
          : 'bg-white text-ink-sub border-border hover:bg-surface-muted'}`}
    >
      {children}
    </button>
  );
}

function Field({
  label, children, className
}: { label: string; children: React.ReactNode; className?: string }) {
  return (
    <label className={`block ${className ?? ''}`}>
      <span className="block text-xs font-demi text-ink-sub mb-1.5">{label}</span>
      {children}
    </label>
  );
}

function PreviewItem({
  label, value, color
}: { label: string; value: string; color: string }) {
  return (
    <div className="text-center">
      <div className="text-[10px] uppercase tracking-wider text-ink-sub font-demi mb-0.5">
        {label}
      </div>
      <div className={`text-sm font-demi ${color}`}>{value}</div>
    </div>
  );
}
